<template>
  <div>
    <v-dialog
      v-model="show"
      :width="$isMobile() ? '100%' : '900px'"
    >
      <v-card 
        class="invoice-review-modal"
        flat 
      > 
        <v-card-title>
          Detalhes da nota fiscal
          <v-spacer />
          <v-icon @click="close">
            close
          </v-icon>
        </v-card-title>
        <v-spacer />
      
        <AppLoadingIcon 
          v-if="isLoading"
          color="#BBB"
        />
        <template v-else-if="invoice">
          <div class="irm-invoice-details">
            <div class="irm-id-company-seller">
              <div class="irm-id-item">
                <p>
                  NFe: <b>{{ invoice.invoiceNumber }}</b> Série: <b>{{ invoice.serialNumber }}</b>
                </p>
                <p class="gray--text text--lighten-1">
                  {{ invoice.company }}
                </p>
              </div>
              <div class="irm-id-item">
                <p class="gray--text text--lighten-1">
                  Vendedor
                </p>
                <p>
                  {{ invoice.seller }}
                </p>
              </div>
            </div>
            <div class="irm-id-item">
              <InvoiceStatus 
                :status="invoice.lastValidationStatus"
                :refusal-reasons="invoice.refusalReason" 
                label="Status Validação"
              />
            </div>
            <div class="irm-id-item">
              <p class="gray--text text--lighten-1">
                Nota
              </p>
              <p>
                1/1
              </p>
            </div>
          </div>
          <v-data-table
            :items="invoice.products"
            :headers="headers"
            class="table-container"
            item-key="_id"
            hide-actions
            disable-initial-sort
          >
            <template
              slot="items"
              slot-scope="props"
            >
              <tr>
                <td 
                  width="40%"
                >
                  <div class="d-inline-flex align-center">
                    <v-checkbox
                      v-if="props.item.toReview"
                      :input-value="true"
                      disabled
                      hide-details
                    />
                    <p class="mb-0">
                      {{ props.item.description }}
                    </p>
                  </div>
                </td>
                <td>
                  {{ props.item.partnersPremiumQuantity }}
                </td>
                <td>
                  {{ 
                    `${props.item.invoiceQuantity}/${props.item.invoiceUnity}` }}
                </td>
                <td>
                  <div class="product-status">
                    <v-icon 
                      :class="status(props.item).color"
                      class="mr-2"
                    >
                      {{ status(props.item).type }}
                    </v-icon>
                    {{ status(props.item).text }}
                  </div>
                </td>
              </tr>
            </template>
          </v-data-table>
  
          <div 
            v-if="invoice.notAgreeWith"
            class="irm-not-agree-with"
          >
            <div class="d-inline-flex align-center">
              <v-checkbox
                :input-value="invoice.notAgreeWith.dmProduct"
                disabled
                hide-details
              />
              <p class="mb-0">
                Produto da Campanha
              </p>
            </div>
  
            <div class="d-inline-flex align-center">
              <v-checkbox
                :input-value="invoice.notAgreeWith.quantity"
                disabled
                hide-details
              />
              <p class="mb-0">
                Quantidade calculada
              </p>
            </div>
            
            <div class="d-inline-flex align-center">
              <v-checkbox
                :input-value="invoice.notAgreeWith.status"
                disabled
                hide-details
              />
              <p class="mb-0">
                Situação
              </p>
            </div>
          </div>
          
          <label class="mx-4 gray--text text--lighten-1">Motivo da revisão</label>
          <textarea 
            :value="invoice.reviewDescription"
            class="irm-reason"
            type="text" 
            rows="3"
            disabled
          />
  
          <div 
            v-if="invoice.files.length"
            class="irm-files"
          >
            <a 
              v-for="file in invoice.files"
              :href="file.path"
              target="_blank"
            >
              <span class="d-flex">
                <v-icon
                  class="mr-2"
                  medium
                >
                  open_in_new
                </v-icon>
                <span>
                  <p class="mb-0"><b>{{ file.filename }}</b></p>
                  <p class="mb-0">Clique para visualizar</p>
                </span>
              </span>
            </a>
          </div>
  
          <div 
            v-if="invoice.reviewStatus === 'pending'"
            class="pa-4 text-xs-right"
          >
            <v-btn
              @click="reprove"
              class="ma-0 mr-2"
              color="primary"
              outline
              large
            >
              Reprovar solicitação
            </v-btn>
            <v-btn
              @click="approve"
              class="ma-0 "
              color="primary"
              large
            >
              Aprovar solicitação
            </v-btn>
          </div>
        </template>
      </v-card>
    </v-dialog>

    <template v-if="invoice">
      <ReproveInvoiceReviewModal
        ref="reproveInvoiceReviewModal"
        :review-id="invoice.reviewId"
        @close="show = true"
        v-on="$listeners"
      />
  
      <ApproveInvoiceReviewModal
        ref="approveInvoiceReviewModal"
        :review-id="invoice.reviewId"
        @close="show = true"
        v-on="$listeners"
      />
    </template>
  </div>
</template>

<script>
import InvoiceStatus from './components/InvoiceStatus.vue'
import ReproveInvoiceReviewModal from './components/ReproveInvoiceReviewModal.vue'
import ApproveInvoiceReviewModal from './components/ApproveInvoiceReviewModal.vue'
import AppLoadingIcon from '@/components/ui/AppLoadingIcon.vue'
import { getInvoiceDetails } from '@/services/containers/partners/invoices'

export default {
  name: 'InvoiceReviewModal',

  components: {
    AppLoadingIcon,
    InvoiceStatus,
    ReproveInvoiceReviewModal,
    ApproveInvoiceReviewModal
  },

  data() {
    return {
      show: false,
      isLoading: false,
      invoice: undefined
    }
  },

  computed: {
    headers() {
      return [
        {
          text: 'Descrição',
          sortable: false,
          value: 'description',
          width: '40%'
        },
        {
          text: 'Quantidade Informada',
          sortable: false,
          value: 'partnersPremiumQuantity'
        },
        {
          text: 'Qtd. / Unidade da Nota',
          sortable: false,
          value: 'invoiceQuantity'
        },
        {
          text: 'Situação validação',
          sortable: false,
          value: 'status'
        }
      ]
    }
  },

  methods: {
    async fetchInvoiceDetails(invoiceId, query = '') {
      try {
        this.isLoading = true
        this.invoice = (await getInvoiceDetails(invoiceId, query))[0]
        
      } catch (error) {
        this.$toast('Falha ao carregar dados da revisão', 'red')
      } finally {
        this.isLoading = false
      }
    },

    open(invoiceId, query = '') {
      this.show = true
      this.fetchInvoiceDetails(invoiceId, query)
    },

    close() {
      this.show = false
    },

    status(product) {
      if (!this.isProductDonMario(product)) {
        return {
          type: 'error',
          color: 'red--text',
          text: 'Não é da campanha'
        }
      }
      const statusOptions = {
        rejected: {
          type: 'error',
          color: 'red--text',
          text: 'Rejeitado'
        },
        valid: {
          type: 'verified',
          color: 'green--text',
          text: 'Aprovado'
        },
        pending: {
          type: 'watch_later',
          color: 'orange--text',
          text: 'Pendente'
        }
      }

      return statusOptions[product.lastValidationStatus]
    },
    isProductDonMario(product) {
      return !!product.partnersPremiumProduct
    },
    productName(product) {
      return this.isProductDonMario(product) 
        ? product.partnersPremiumName || '-' 
        : 'Não é um produto da campanha'
    },

    async approve() {
      this.close()
      this.$refs.approveInvoiceReviewModal.open()
    },

    async reprove() {
      this.close()
      this.$refs.reproveInvoiceReviewModal.open()
    }
  }
}
</script>

<style lang="stylus" scoped>
.invoice-review-modal {
  .irm-invoice-details {
    align-items: center;
    border-bottom: 1px solid var(--v-gray-lighten3);
    column-gap: 16px;
    display: flex;
    justify-content: space-between;
    padding: 24px;
    
    .irm-id-company-seller {
      display: flex;
      
      >:nth-child(1) {
        margin-right: 36px;
      }

      >:nth-child(2) {
        padding-left: 36px;
        border-left: 1px solid var(--v-gray-lighten3);
      }
    }

    .irm-id-item {
      display: flex;
      flex-direction: column

      p {
        margin: 0;
      }
    }

  }

  .irm-not-agree-with {
    align-items: center;
    border-top: 1px solid var(--v-gray-lighten3);
    display: flex;
    justify-content: space-between;
    padding: 24px 24px 16px;
  }

  .irm-reason {
    border-radius: 4px;
    border: 1px solid var(--v-gray-lighten1);
    color: var(--v-gray-lighten1);
    margin: 16px 24px;
    padding: 16px;
    width: calc(100% - 48px);
  }

  .irm-files {
    display: flex;
    flex-wrap: wrap;
    column-gap: 24px;
    row-gap: 16px;
    overflow-wrap: anywhere;
    padding: 0 24px;

    a {
      min-width: 190px;
      color: var(--v-gray-lighten1);
      text-decoration: none;
    }
  }


  .v-input--selection-controls {
    margin: 0 !important;
    padding: 0 !important;
  }

  .table-container {
    tbody-scroll-width = 10px;
    ::v-deep {
      thead, tbody tr {
        width: 100%;
        display: table;
        table-layout: fixed;
      }

      thead {
        width: "calc(100% - %s)" % tbody-scroll-width
      }

      tbody {
        max-height: 280px;
        overflow-y: auto;
        display: block;
      }

      /* width */
      &::-webkit-scrollbar {
        width: tbody-scroll-width;
      }
      
      /* Track */
      &::-webkit-scrollbar-track {
        background: #ededed;
      }

      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
      }

      /* Handle on hover */
      &::-webkit-scrollbar-thumb:hover {
        background: #555;
      }

      .product-status {
        display: flex;
        align-items: center;
      }
    }
  }
}

</style>

<style lang="stylus">
.btn-cancel {
  border: 1px solid var(--v-primary-base) !important;
  box-shadow: none !important;
  background-color: white !important;
  color: var(--v-primary-base) !important;
}
</style>